/**
 * The capture ANCHOR, as one experience — used by every surface that files a
 * capture (#967, #964).
 *
 * Christopher, 2026-08-08 on #967: *"as MB admin you should be able to select
 * from the orgs and projects assigned to you"*. This is that selector, and there
 * is exactly one of it: the standalone extension and `apps/console`'s `/share`
 * render THIS, not two things that look alike.
 *
 * ── it is a CONFIGURATION of the shared controls, not a new one ────────────
 *
 * #964's binding comment is explicit that a picker comes from `Lookup` /
 * `RelationField` and that *"a `<select>` over a pre-fetched list"* is not
 * acceptable. Both fields below are `RelationField` — the console's own N:1
 * control, the same one `RefPicker` puts on every generic record form — over the
 * targets the door returned. Nothing here draws a list, a chip, a caret or a
 * popover of its own.
 *
 * (Small correction for the record: that comment names `RefPicker` as a shared
 * component. `RefPicker` is a CONSOLE file — `apps/console/src/form/RefPicker.tsx`
 * — and the shared control it wraps is `RelationField`. Same intent, and worth
 * saying so nobody goes looking for a `@solum/ui` export that does not exist.)
 *
 * ── where a surface has less room, that is SUBTRACTION ─────────────────────
 *
 * The one thing an extension panel genuinely has less of is WIDTH: a Chrome side
 * panel is whatever the user dragged it to. Two changes were made to the shared
 * components rather than around them —
 *
 *   · `Lookup`'s popover now carries `maxWidth: calc(100vw - 16px)`. It was a
 *     fixed 360px with only its LEFT edge clamped, so a narrower viewport pushed
 *     it off the other side. Nothing opts in and nothing renders differently
 *     where the viewport was already wide enough. A phone in portrait had the
 *     same defect.
 *   · `RelationField` forwards an optional `width` to it, so a host asks for
 *     less rather than drawing a narrower picker of its own.
 *
 * Both are covered by the console's browser suite, which is the safety net the
 * brief asks for: `apps/console/e2e-browser` drives `RelationField` through the
 * Gallery and through the generic record form.
 *
 * The other subtraction is a PROP NOT PASSED: the project field renders only
 * when the caller HAS projects. A customer contact with none sees one field, not
 * an empty second one — and the day they have a project it is the same field,
 * not a rewrite.
 *
 * ── the rule is the door's, not this component's ───────────────────────────
 *
 * The preselect, which organization a project pins, and what may be sent are all
 * in `captureTargets.ts` beside this file, which reads them off the server's own
 * answer. This file renders and calls; it decides nothing.
 */
import { useMemo } from 'react'
import { Field } from '../controls/Field'
import { RelationField } from '../controls/RelationField'
import { EmptyState } from '../controls/EmptyState'
import { ErrorState } from '../controls/ErrorState'
import { Spinner } from '../controls/Spinner'
import type { LookupItem, LookupSection } from '../controls/Lookup'
import {
  anchorRefusal, organizationsOf, projectsOf, selectOrganization, selectProject,
  type CaptureAnchorSelection, type CaptureTarget,
} from './captureTargets'
import s from './CaptureAnchorFields.module.css'

export interface CaptureAnchorFieldsProps {
  /** The rows `feedback.my_capture_targets()` answered. */
  targets: CaptureTarget[]
  value: CaptureAnchorSelection
  onChange: (next: CaptureAnchorSelection) => void
  loading?: boolean
  /** A failure READING the door — distinct from "you are assigned to nothing". */
  error?: string | null
  /** Popover width, for a host with less room than a desktop app. */
  width?: number
  testid?: string
}

const asItem = (t: CaptureTarget): LookupItem => ({ id: t.id, primary: t.label })

export function CaptureAnchorFields({
  targets, value, onChange, loading, error, width, testid,
}: CaptureAnchorFieldsProps) {
  const orgs = useMemo(() => organizationsOf(targets), [targets])
  const projects = useMemo(() => projectsOf(targets), [targets])

  const searchOrgs = useMemo(() => (q: string): LookupSection[] => [{
    items: match(orgs, q).map(asItem),
  }], [orgs])

  // The project list narrows to the chosen organization. Not a filter for
  // tidiness — the server refuses a capture naming a project and a different
  // organization, and offering the pair that will be refused is offering a
  // mistake.
  const searchProjects = useMemo(() => (q: string): LookupSection[] => [{
    items: match(projects.filter(
      (p) => !value.organizationId || p.account_id === value.organizationId), q).map(asItem),
  }], [projects, value.organizationId])

  if (loading) return <div data-testid={testid}><Spinner /></div>
  if (error) {
    return (
      <ErrorState
        title="Could not read your organizations"
        message={`${error} Sending would be refused, so the capture is held here rather than lost.`}
      />
    )
  }

  const refusal = anchorRefusal(targets, value)
  if (targets.length === 0) return <EmptyState>{refusal}</EmptyState>

  return (
    <div data-testid={testid} className={s.fields}>
      <Field label="Organization" required>
        <RelationField
          testid={testid ? `${testid}-org` : undefined}
          value={value.organizationId}
          width={width}
          placeholder="Which organization is this about?"
          search={searchOrgs}
          display={(id) => asItem(orgs.find((o) => o.id === id) ?? fallback(id, targets))}
          onChange={(id) => { onChange(selectOrganization(targets, value, id)) }}
        />
      </Field>

      {projects.length > 0 && (
        <Field
          label="Project"
          hint="Optional. A capture about a delivery project reaches the people who own it."
        >
          <RelationField
            testid={testid ? `${testid}-project` : undefined}
            value={value.projectId}
            width={width}
            placeholder="Which project, if any?"
            search={searchProjects}
            display={(id) => asItem(projects.find((p) => p.id === id) ?? fallback(id, targets))}
            onChange={(id) => { onChange(selectProject(targets, id)) }}
          />
        </Field>
      )}
    </div>
  )
}

/** Client-side narrowing over a list the caller already holds — no round trip. */
function match(rows: CaptureTarget[], q: string): CaptureTarget[] {
  const needle = q.trim().toLowerCase()
  if (needle === '') return rows
  return rows.filter((r) => r.label.toLowerCase().includes(needle))
}

/**
 * A selected id that is not in the list any more. Showing the id is honest;
 * showing an empty chip would claim the field is unset — the same argument
 * `RefPicker` makes for the in-flight case.
 */
const fallback = (id: string, targets: CaptureTarget[]): CaptureTarget =>
  targets.find((t) => t.id === id) ?? {
    kind: 'organization', id, label: id, account_id: id, account_label: id, is_default: false,
  }
